import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { ICmdbLicenseModelState } from './licenseModel.model';
import {
  deleteCmdbLicenseModel,
  getCmdbLicenseModelById,
  saveCmdbLicenseModel,
  searchCmdbLicenseModel,
} from './licenseModel.action';
import { ICmdbLicenseModel } from '../../../services/cmdb/licenseModel/licenseModel.model';

export const initialState: ICmdbLicenseModelState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const cmdbLicenseModelSlice = createSlice({
  name: 'cmdbLicenseModel',
  initialState,
  reducers: {
    clearCmdbLicenseModel: () => {
      return initialState;
    },
    clearCmdbLicenseModelMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearCmdbLicenseModelGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCmdbLicenseModel.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCmdbLicenseModel.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.lookups = { booleanLookup };
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchCmdbLicenseModel.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCmdbLicenseModelById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCmdbLicenseModelById.fulfilled.type]: (state, action: PayloadAction<ICmdbLicenseModel>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCmdbLicenseModelById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCmdbLicenseModel.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCmdbLicenseModel.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCmdbLicenseModel.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCmdbLicenseModel.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCmdbLicenseModel.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCmdbLicenseModel.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const cmdbLicenseModelSelector = (state: RootState) => state.cmdbLicenseModel;

// Actions
export const {
  clearCmdbLicenseModel,
  clearCmdbLicenseModelMessages,
  clearCmdbLicenseModelGetById,
  setTableColumnSelection,
} = cmdbLicenseModelSlice.actions;

// The reducer
export default cmdbLicenseModelSlice.reducer;
